import React, { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { Grid, Hidden, makeStyles, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { ACCESS_KEY, SHARE_SUCCESS_MESSAGE, SHARE_LOADING_MESSAGE } from 'app-constants';
import { PaddedPaper, SocialIcon, ValidatedTextFieldNext } from 'components';
import {
  useListPublicSocialConnectionsQuery,
  ListPublicSocialConnectionsQuery,
  Network,
  useCreatePublicShareInfoMutation,
  ShareType,
  ShareContentDestination,
  CreatePublicShareInfoMutation,
  usePublicShareContentMutation,
} from 'api/public';
import { getSocialAccountName } from 'helpers';
import { useNotify } from 'hooks';
import { popup } from 'utilities';
import { TextCounter } from 'features/posting/components/Partials';
import { getCommentCharLimit } from 'features/posting/helpers';

import { usePublicPostContext } from '../PublicPostContext';
import PublicShareButton from './PublicShareButton';
import PublicShareErrorMessage from './PublicShareErrorMessage';
import PublicShareSuccessMessage from './PublicShareSuccessMessage';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(2),
  },
  textbox: {
    backgroundColor: theme.palette.common.white,
  },
  hasShared: {
    opacity: 0.4,
  },
  socialLabel: {
    marginLeft: theme.spacing(0.5),
  },
}));

type IProps = {
  network: Network;
};

const PublicShareTextBox: FC<IProps> = ({ network }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const { state } = usePublicPostContext();

  const { content } = state;
  const { content_id } = content;

  const { commentary, lock_commentary } = content;

  const initialCommentary = useMemo(() => {
    return (commentary || []).find(comment => comment.network === network)?.commentary || '';
  }, [network]);

  const [message, setMessage] = useState(initialCommentary);
  const [hasShared, setHasShared] = useState(false);
  const [isShareLoadingFromConnection, setIsShareLoadingFromConnection] = useState(false);

  const limit = getCommentCharLimit(network, state.content);

  const accessKey = localStorage.getItem(ACCESS_KEY);

  const networkProperties = {
    [Network.Twitter]: {
      limit: 280,
      title: t('admin:twitter'),
      placeholder: t('posting:twitterShare'),
    },
    [Network.Linkedin]: {
      limit: 1300,
      title: t('admin:linkedin'),
      placeholder: t('posting:linkedinShare'),
    },
    [Network.Facebook]: {
      limit: 1000,
      title: t('admin:facebook'),
      placeholder: t('posting:facebookShare'),
    },
  };

  useEffect(() => {
    window.addEventListener('message', event => void updateShare(event), false);
    return () => {
      window.removeEventListener('message', event => void updateShare(event), false);
    };
  });

  function updateShare(event: MessageEvent) {
    if (!event.data) {
      return;
    }

    const { network: messageNetwork, content_id: messageContentId, message } = event.data;

    if (network === messageNetwork && content_id === messageContentId) {
      if (message === SHARE_LOADING_MESSAGE) {
        setIsShareLoadingFromConnection(true);
      }

      if (message === SHARE_SUCCESS_MESSAGE) {
        setIsShareLoadingFromConnection(false);
        setHasShared(true);
      }
    }
  }

  function onListSocialSuccess(data: ListPublicSocialConnectionsQuery) {
    const { listPublicSocialConnections } = data;
    const { access_key } = listPublicSocialConnections;

    localStorage.setItem(ACCESS_KEY, access_key);
  }

  const variables = accessKey !== 'null' ? { access_key: accessKey } : {};

  const { data } = useListPublicSocialConnectionsQuery(variables, {
    onError: notify.queryError,
    onSuccess: onListSocialSuccess,
  });

  function onShareSuccess() {
    setHasShared(true);
    notify.info({ component: <PublicShareSuccessMessage network={network} />, duration: 8000 });
  }

  function onShareError() {
    notify.error({ component: <PublicShareErrorMessage /> });
  }

  function onCreateShareInfoSuccess(data: CreatePublicShareInfoMutation) {
    const short_link = data.createPublicShareInfo?.shortened_urls?.facebook?.short_link;
    const { content_object } = content;
    const { url } = content_object;

    const href = short_link || url || window?.location?.href;

    window?.FB?.ui(
      {
        href,
        method: 'share',
        quote: message,
      },
      () => {
        const variables = {
          access_key: accessKey,
          content_id,
          items: [
            {
              share_type: ShareType.Frontend,
              destination: ShareContentDestination.Facebook,
            },
          ],
        };

        mutate(variables);
      }
    );
  }

  const { mutate, isLoading } = usePublicShareContentMutation({
    onError: onShareError,
    onSuccess: onShareSuccess,
  });

  const { mutate: createShareInfoMutate, isLoading: isCreateShareInfoLoading } =
    useCreatePublicShareInfoMutation({
      onError: onShareError,
      onSuccess: onCreateShareInfoSuccess,
    });

  function connectNetwork() {
    popup(
      `connect/${network}/${content.content_id}/${encodeURIComponent(message)}`,
      'Connection Popup',
      650,
      650
    );
  }

  const loading = isLoading || isCreateShareInfoLoading || isShareLoadingFromConnection;

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    setMessage(event.target.value);
  }

  function onClickShareToTwitterAndLinkedin() {
    if (userNetworkInfo) {
      return mutate({
        content_id,
        access_key: accessKey,
        items: [
          { network_id: userNetworkInfo.id, user_commentary: message, share_type: ShareType.Now },
        ],
      });
    }

    connectNetwork();
  }

  function onClickShareToFacebook() {
    createShareInfoMutate({
      access_key: accessKey,
      content_id,
    });
  }

  function onClickShare() {
    if (network === Network.Facebook) {
      return onClickShareToFacebook();
    }

    onClickShareToTwitterAndLinkedin();
  }

  const items = data?.listPublicSocialConnections?.items || [];

  const userNetworkInfo = items.find(item => item.provider === network);

  const networkInfo = networkProperties[network];

  if (!networkInfo) {
    return null;
  }

  const title = getSocialAccountName(userNetworkInfo) || networkInfo.title;

  return (
    <Formik initialValues={{ message: initialCommentary }} onSubmit={onClickShare}>
      <PaddedPaper>
        <Grid container direction="column" spacing={1}>
          <Grid item xs>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <SocialIcon state="active" provider={network} />
                  </Grid>
                  <Grid item>
                    <Typography className={classes.socialLabel} variant="subtitle2">
                      {title}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Hidden mdUp>
                  <PublicShareButton
                    network={network}
                    hasShared={hasShared}
                    isLoading={loading}
                    isLight
                  />
                </Hidden>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs>
              <Grid direction="column" spacing={2} container>
                <Grid item>
                  <ValidatedTextFieldNext
                    name="message"
                    fullWidth
                    multiline
                    rows="3"
                    rowsMax="40"
                    margin="none"
                    placeholder={networkInfo.placeholder}
                    onChange={onChange}
                    className={hasShared && classes.hasShared}
                    disabled={hasShared || lock_commentary}
                  />
                </Grid>
                <Grid item>
                  <TextCounter length={message.length} limit={limit} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Hidden smDown>
                <PublicShareButton network={network} hasShared={hasShared} isLoading={loading} />
              </Hidden>
            </Grid>
          </Grid>
        </Grid>
      </PaddedPaper>
    </Formik>
  );
};

export default PublicShareTextBox;
